import React from 'react';

import {useTranslation} from 'react-i18next';

import {Spinner} from 'components/ui-kit/Spinner';
import {PackageTypes, TRANSPORT_PACKAGE_TYPES} from 'types/gtins';
import {ProductFormatted} from 'types/products-detail';

import {
    Column,
    ColumnElement,
    ColumnsWrapper,
    HeaderTypography,
    SpinnerContainer,
    Title,
    Typography,
} from './styled';

interface Props {
    data: ProductFormatted;
    isLoading: boolean;
    packagingType?: PackageTypes;
}

export const CommonInfo = ({data, packagingType, isLoading}: Props) => {
    const {t} = useTranslation();
    return isLoading ? (
        <SpinnerContainer>
            <Spinner />
        </SpinnerContainer>
    ) : (
        <ColumnsWrapper noGap={true}>
            {!data.isTransportPackage && (
                <Column>
                    <ColumnElement>
                        <Title>{t('pages.products.detail.common.product')}</Title>
                    </ColumnElement>
                    <ColumnElement>
                        <HeaderTypography>
                            {t('pages.products.detail.common.productGroup')}:
                        </HeaderTypography>
                        <Typography>{data.productGroupCode}</Typography>
                    </ColumnElement>
                    <ColumnElement>
                        <HeaderTypography>
                            {t('pages.products.detail.common.packageType')}:
                        </HeaderTypography>
                        <Typography highlighted>{data.packagingType}</Typography>
                    </ColumnElement>
                    <ColumnElement>
                        <HeaderTypography>
                            {t('pages.products.detail.common.batchNumber')}:
                        </HeaderTypography>
                        <Typography highlighted>{data.batchNumber}</Typography>
                    </ColumnElement>
                    <ColumnElement>
                        <HeaderTypography>
                            {t('pages.products.detail.common.manufacturingDate')}:
                        </HeaderTypography>
                        <Typography highlighted>{data.manufacturingDate}</Typography>
                    </ColumnElement>
                    <ColumnElement>
                        <HeaderTypography>
                            {t('pages.products.detail.common.brand')}:
                        </HeaderTypography>
                        <Typography highlighted>{data.brand}</Typography>
                    </ColumnElement>
                    <ColumnElement>
                        <HeaderTypography>
                            {t('pages.products.detail.common.name')}:
                        </HeaderTypography>
                        <Typography highlighted>{data.name}</Typography>
                    </ColumnElement>
                    <ColumnElement>
                        <HeaderTypography>
                            {t('pages.products.detail.common.productCode')}:
                        </HeaderTypography>
                        <Typography highlighted>{data.gtin}</Typography>
                    </ColumnElement>
                </Column>
            )}
            {data.storageLocation && (
                <Column>
                    <ColumnElement>
                        <Title>{t('pages.products.detail.common.storageLocation.location')}</Title>
                    </ColumnElement>
                    <ColumnElement>
                        <HeaderTypography>
                            {t('pages.products.detail.common.storageLocation.address')}:
                        </HeaderTypography>
                        <Typography highlighted>{data.storageLocation}</Typography>
                    </ColumnElement>
                    <ColumnElement>
                        <HeaderTypography>
                            {t('pages.products.detail.common.storageLocation.name')}:
                        </HeaderTypography>
                        <Typography highlighted>{data.storageName}</Typography>
                    </ColumnElement>
                </Column>
            )}
            {packagingType && TRANSPORT_PACKAGE_TYPES.includes(packagingType) && (
                <Column>
                    <ColumnElement>
                        <Title>{t('pages.products.detail.common.package')}</Title>
                    </ColumnElement>
                    <ColumnElement>
                        <HeaderTypography>
                            {t('pages.products.detail.common.packagingLevel')}:
                        </HeaderTypography>
                        <Typography highlighted>{data.packagingLevel}</Typography>
                    </ColumnElement>
                </Column>
            )}
            {data.authentic && (
                <Column>
                    <ColumnElement>
                        <Title>{t('pages.products.detail.common.authenticity')}</Title>
                    </ColumnElement>
                    <ColumnElement>
                        <HeaderTypography>
                            {t('pages.products.detail.common.status')}:
                        </HeaderTypography>
                        {data.authentic.status}
                    </ColumnElement>
                    <ColumnElement>
                        <HeaderTypography>
                            {t('pages.products.detail.common.statusDate')}:
                        </HeaderTypography>
                        <Typography highlighted>{data.authentic.date}</Typography>
                    </ColumnElement>
                    <ColumnElement>
                        <HeaderTypography>
                            {t('pages.products.detail.common.eventType')}:
                        </HeaderTypography>
                        <Typography highlighted>{data.authentic.type}</Typography>
                    </ColumnElement>
                    <ColumnElement>
                        <HeaderTypography>
                            {t('pages.products.detail.common.documentLink')}:
                        </HeaderTypography>
                        <Typography highlighted>{data.authentic.link}</Typography>
                    </ColumnElement>
                </Column>
            )}
        </ColumnsWrapper>
    );
};
